import { invoiceTemplateConfig as config } from '../../config/invoiceTemplate';
import { brandingConfig } from '../../config/branding';

export const generateInvoiceHTML = (parcel) => {
  const {
    parcelId,
    trackingNumber,
    parcelStatus = 'Pending',
    parcelPickupAddress,
    parcelDeliveryAddress,
    parcelWeight,
    distance,
    parcelPrice = 0,
    parcelCreatedAt,
    senderDetails,
    receiverDetails,
    paymentStatus = 'Paid',
    paymentMethod = 'Wallet',
  } = parcel;

  const statusKey = (parcelStatus || 'pending').toLowerCase().replace(' ', '_');
  const statusColors = config.colors.status[statusKey] || config.colors.status.pending;

  // Formatting helpers
  const formatDate = (date) =>
    new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount);

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <title>Invoice - ${trackingNumber || parcelId}</title>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap" rel="stylesheet">
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
          font-family: ${config.typography.fontFamily};
          color: ${config.colors.text.main};
          line-height: 1.5;
          background: #fff;
          padding: 20px;
        }
        .invoice-container {
          max-width: 900px;
          margin: 0 auto;
          display: flex;
          min-height: 1000px;
          border: 1px solid #eee;
          box-shadow: 0 10px 25px rgba(0,0,0,0.05);
          overflow: hidden;
          background: #fff;
        }
        
        /* Sidebar Styles */
        .sidebar {
          width: ${config.layout.sidebarWidth};
          background: ${config.colors.primary};
          color: #fff;
          padding: 40px 30px;
          display: flex;
          flex-direction: column;
        }
        .sidebar-logo {
          font-size: 24px;
          font-weight: 800;
          margin-bottom: 50px;
          letter-spacing: -1px;
        }
        .sidebar-section { margin-bottom: 40px; }
        .sidebar-label {
          font-size: 11px;
          text-transform: uppercase;
          color: #888;
          letter-spacing: 1px;
          margin-bottom: 8px;
          font-weight: 700;
        }
        .sidebar-value { font-size: 15px; font-weight: 600; margin-bottom: 5px; }
        .sidebar-badge {
          display: inline-block;
          padding: 6px 14px;
          border-radius: 50px;
          font-size: 11px;
          font-weight: 700;
          background: ${statusColors.bg};
          color: ${statusColors.text};
          text-transform: uppercase;
        }
        .verification-container {
          margin-top: auto;
          background: #fff;
          padding: 16px;
          border-radius: 12px;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }
        .qr-img-large { 
          width: 120px; 
          height: 120px;
        }
        .verification-text {
          font-size: 10px;
          color: #1a1a1a;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .verification-subtext {
          font-size: 9px;
          color: #666;
          line-height: 1.2;
        }
        .main-content {
          flex: 1;
          padding: 40px 50px;
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 60px;
        }
        .header-title {
          font-size: 32px;
          font-weight: 800;
          color: ${config.colors.primary};
        }
        .address-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 40px;
          margin-bottom: 60px;
        }
        .address-label {
          font-size: 12px;
          color: ${config.colors.text.muted};
          text-transform: uppercase;
          font-weight: 700;
          margin-bottom: 12px;
          border-bottom: 1px solid #eee;
          padding-bottom: 5px;
        }
        .address-name { font-weight: 700; font-size: 16px; margin-bottom: 5px; }
        .address-text { font-size: 14px; color: ${config.colors.text.muted}; }

        /* Table Styles */
        table { width: 100%; border-collapse: collapse; margin-bottom: 40px; }
        th {
          text-align: left;
          font-size: 12px;
          text-transform: uppercase;
          color: ${config.colors.text.muted};
          padding: 15px 0;
          border-bottom: 2px solid ${config.colors.primary};
        }
        td { padding: 20px 0; border-bottom: 1px solid #eee; font-size: 14px; }
        .price-col { text-align: right; font-weight: 700; }
        
        .totals-section {
          margin-left: auto;
          width: 250px;
        }
        .total-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 10px;
        }
        .total-label { color: ${config.colors.text.muted}; }
        .total-value { font-weight: 700; }
        .grand-total {
          margin-top: 20px;
          padding-top: 20px;
          border-top: 2px solid #eee;
          font-size: 20px;
          color: ${config.colors.secondary};
        }
        
        @media print {
          body { padding: 0; }
          .invoice-container { box-shadow: none; border: none; width: 100%; max-width: 100%; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="invoice-container">
        <div class="sidebar">
          <div class="sidebar-logo">${brandingConfig.brandName}</div>
          
          <div class="sidebar-section">
            <div class="sidebar-label">Tracking Number</div>
            <div class="sidebar-value">${trackingNumber || parcelId}</div>
          </div>
          
          <div class="sidebar-section">
            <div class="sidebar-label">Status</div>
            <div class="sidebar-badge">${parcelStatus}</div>
          </div>
          
          <div class="sidebar-section">
            <div class="sidebar-label">Payment Method</div>
            <div class="sidebar-value">${paymentMethod}</div>
            <div class="sidebar-label" style="margin-top: 10px;">Payment Status</div>
            <div class="sidebar-value">${paymentStatus}</div>
          </div>

          <div class="verification-container">
             <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${trackingNumber || parcelId}" class="qr-img-large" alt="Verification QR">
             <div class="verification-text">Scan to Verify</div>
             <div class="verification-subtext">Authentic Digital Shipment Record<br>ID: ${trackingNumber || parcelId}</div>
          </div>
        </div>
        
        <div class="main-content">
          <div class="header">
            <div>
              <div class="header-title">INVOICE</div>
              <div style="color: ${config.colors.text.muted}">${formatDate(parcelCreatedAt)}</div>
            </div>
          </div>
          
          <div class="address-grid">
            <div>
              <div class="address-label">Sender</div>
              <div class="address-name">${senderDetails?.name || 'Customer'}</div>
              <div class="address-text">${parcelPickupAddress?.label || 'Pickup Location'}</div>
            </div>
            <div>
              <div class="address-label">Receiver</div>
              <div class="address-name">${receiverDetails?.name || 'Recipient'}</div>
              <div class="address-text">${parcelDeliveryAddress?.label || 'Delivery Location'}</div>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Item Description</th>
                <th>Qty</th>
                <th class="price-col">Weight/Dist</th>
                <th class="price-col">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Delivery Services (Tracking: ${trackingNumber || parcelId})</td>
                <td>1</td>
                <td class="price-col">${parcelWeight || 0}kg / ${distance || 0}km</td>
                <td class="price-col">${formatCurrency(parcelPrice)}</td>
              </tr>
            </tbody>
          </table>
          
          <div class="totals-section">
            <div class="total-row">
              <span class="total-label">Subtotal</span>
              <span class="total-value">${formatCurrency(parcelPrice)}</span>
            </div>
            <div class="total-row">
              <span class="total-label">Tax (0%)</span>
              <span class="total-value">${formatCurrency(0)}</span>
            </div>
            <div class="total-row grand-total">
              <span>Total Amount</span>
              <span>${formatCurrency(parcelPrice)}</span>
            </div>
          </div>
          
          <div style="margin-top: 100px; font-size: 12px; color: #888;">
            <p style="font-weight: 700; color: #333; margin-bottom: 5px;">Thank you for your business!</p>
            <p>If you have any questions, please contact us at support@${brandingConfig.brandName.toLowerCase()}.com</p>
          </div>
        </div>
      </div>
    </body>
    </html>
  `;
};
